/* eslint-disable lines-between-class-members,@typescript-eslint/camelcase */

import { VueConstructor } from 'vue';
import { Vue } from 'vue-property-decorator';

import {
  AuthorizationServiceConfiguration,
  FetchRequestor,
  TokenRequestHandler,
  TokenRequest,
  TokenResponse,
  RevokeTokenRequest,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
} from '@openid/appauth/built';

import { v4 as uuid } from 'uuid';

import CustomTokenRequestHandler from './handlers/token';

import { AuthResult, AuthState, OAuth as Interface, OAuthParams, TokenPayload } from './interface';
import OAuthStorage from './storage';
import mixin from './mixin';
import { warn } from './helpers';

export default class OAuth implements Interface {
  static installed = false;

  public readonly authState: AuthState = Vue.observable({
    accessToken: null,
    tokenPayload: null,
  });

  private readonly configuration: AuthorizationServiceConfiguration;
  private readonly tokenHandler: TokenRequestHandler;

  private readonly storage: OAuthStorage;

  private readonly clientId: string;
  private readonly redirectUri: string;

  private state: string | null = null;

  private tokenResponse: TokenResponse | null = null;

  constructor({ clientId, redirectUri, serviceConfiguration }: OAuthParams) {
    this.configuration = new AuthorizationServiceConfiguration(serviceConfiguration);
    this.tokenHandler = new CustomTokenRequestHandler(new FetchRequestor());

    this.storage = new OAuthStorage(localStorage);

    this.clientId = clientId;
    this.redirectUri = redirectUri;

    this._restoreSavedState();
  }

  refreshState(): string {
    const state = uuid();

    this.state = state;

    this.storage.storeState(state);

    return state;
  }

  async generateToken(code: string, state: string): Promise<AuthResult> {
    if (!state || this.state !== state) {
      throw new Error('Invalid state');
    }

    this.storage.clearState();

    const request = new TokenRequest({
      code,
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
    });

    return this.tokenHandler
      .performTokenRequest(this.configuration, request)
      .then(response => this._handleTokenResponse(response));
  }

  async refreshToken(): Promise<AuthResult> {
    if (!this.tokenResponse) {
      throw new Error('Not authorized');
    }

    const request = new TokenRequest({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      refresh_token: this.tokenResponse.refreshToken,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
    });

    return this.tokenHandler
      .performTokenRequest(this.configuration, request)
      .then(response => this._handleTokenResponse(response));
  }

  async revokeToken(): Promise<boolean> {
    if (!this.tokenResponse) {
      throw new Error('Not authorized');
    }

    const request = new RevokeTokenRequest({
      token: this.tokenResponse.accessToken,
    });

    return this.tokenHandler
      .performRevokeTokenRequest(this.configuration, request)
      .finally(() => this.resetToken());
  }

  checkAuthorized(): boolean {
    return !!this.tokenResponse;
  }

  checkTokenValid(): boolean {
    return !!this.tokenResponse && this.tokenResponse.isValid();
  }

  resetToken(): void {
    this._updateTokenResponse(null);

    this.storage.clear();
  }

  private _handleTokenResponse(response: TokenResponse): AuthResult {
    return this._updateTokenResponse(response) as AuthResult;
  }

  private _updateTokenResponse(response: TokenResponse | null): AuthState {
    this.tokenResponse = response;

    if (response) {
      this.storage.storeResponse(response);
    } else {
      this.storage.clearResponse();
    }

    return this._updateAuthState(response && response.accessToken);
  }

  private _restoreSavedState() {
    this.state = this.storage.retrieveState();

    const response = this.storage.retrieveResponse();
    this._updateTokenResponse(response);
  }

  private _updateAuthState(accessToken: string | null): AuthState {
    const tokenPayload = accessToken ? this._parseTokenPayload(accessToken) : null;
    const authState = { accessToken, tokenPayload };

    Object.assign(this.authState, authState);

    return authState;
  }

  private _parseTokenPayload(accessToken: string): TokenPayload {
    const [payload] = accessToken.split('.').slice(1);

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => {
          const char = c.charCodeAt(0).toString(16);
          const code = `00${char}`.slice(-2);

          return `%${code}`;
        })
        .join('')
    );

    return JSON.parse(json);
  }

  /*
   * Vue plugin install function
   */
  static install(Vue: VueConstructor): void {
    if (process.env.NODE_ENV !== 'production' && this.installed) {
      warn('Already installed');

      return;
    }

    this.installed = true;

    Vue.mixin(mixin);

    Object.defineProperties(Vue.prototype, {
      $oauth: {
        get() {
          return this._oauth;
        },
      },
    });

    const strategies = Vue.config.optionMergeStrategies;

    // eslint-disable-next-line func-names
    strategies.oauth = function(parentVal: never, childVal: never) {
      return childVal === undefined ? parentVal : childVal;
    };
  }
}
